import React, { useEffect, ReactNode, useState } from "react";
import { Colors } from "../configs";
import { Box, Typography } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { CustomButton } from "../components";
import CustomModal from "../components/common/custom-modal/custom-modal";
import { Maximize2, X } from "lucide-react";
import { FLUID_LAYOUT_EVENT, isFluidLayout } from "../util/fluid-layout";

interface ResponsiveProviderProps {
  children: ReactNode;
}


const ResponsiveProvider: React.FC<ResponsiveProviderProps> = ({
  children,
}) => {
  const [showAspectRatioWarning, setShowAspectRatioWarning] = useState(false);
  const [showFullScreenWarning, setShowFullScreenWarning] = useState(false);

  const toggleFullScreen = () => {
    const element = document.body;

    if (document.fullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    } else {
      if (element.requestFullscreen) {
        element.requestFullscreen();
      }
    }
  };

  useEffect(() => {
    const adjustRootSizeAndDetectFullScreen = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const aspectRatio = width / height;
      const desiredAspectRatio = 16 / 9;

      const fluid = isFluidLayout();

      // Detect if the screen is in fullscreen mode. Screens that fill any
      // window (the admin) don't need it - and they re-run this on every
      // page change, which kept bringing the prompt back
      if (document.fullscreenElement || fluid) {
        setShowFullScreenWarning(false);
      } else {
        setShowFullScreenWarning(true);
      }

      // Define a small tolerance for floating-point precision
      const epsilon = 0.01;

      let newWidth, newHeight;

      // Check if the screen is 16:9 (or the screen fills any window)
      if (fluid || Math.abs(aspectRatio - desiredAspectRatio) < epsilon) {
        newWidth = width;
        newHeight = height;
        setShowAspectRatioWarning(false);
      } else {
        // Calculate the largest 16:9 size that can fit in the screen
        if (aspectRatio > desiredAspectRatio) {
          newHeight = height;
          newWidth = height * desiredAspectRatio;
        } else {
          newWidth = width;
          newHeight = width / desiredAspectRatio;
        }
        document.fullscreenElement && setShowAspectRatioWarning(true);
      }

      // Calculate font size based on root's height
      const calculatedFontSize = newHeight * 0.012962963;

      // Apply styles to root and html
      const root = document.getElementById("root");
      if (root) {
        root.style.width = `${newWidth}px`;
        root.style.height = `${newHeight}px`;
        root.style.position = "absolute";
        root.style.top = "50%";
        root.style.left = "50%";
        root.style.transform = "translate(-50%, -50%)";
        root.style.background = Colors.WHITE;
      }

      document.documentElement.style.fontSize = `${calculatedFontSize}px`;
      document.documentElement.style.overflow = "hidden";
      document.body.style.background = Colors.ACCENT_GRAY_DISABLED;
    };

    // Event listener to detect fullscreen changes
    const handleFullscreenChange = () => {
      adjustRootSizeAndDetectFullScreen();
    };

    // Add event listeners
    adjustRootSizeAndDetectFullScreen();
    window.addEventListener("resize", adjustRootSizeAndDetectFullScreen);
    window.addEventListener(FLUID_LAYOUT_EVENT, adjustRootSizeAndDetectFullScreen);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("keydown", (event) => {
      // this is here because the fullscreenchange event is not triggered when pressing F11
      if (event.key === "F11") {
        event.preventDefault();
        event.stopPropagation();
        toggleFullScreen();
      }
    });

    return () => {
      // Cleanup event listeners
      window.removeEventListener("resize", adjustRootSizeAndDetectFullScreen);
      window.removeEventListener(FLUID_LAYOUT_EVENT, adjustRootSizeAndDetectFullScreen);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("keydown", (event) => {
        // this is here because the fullscreenchange event is not triggered when pressing F11
        if (event.key === "F11") {
          event.preventDefault();
          event.stopPropagation();
          toggleFullScreen();
        }
      });
    };
  }, []);

  return (
    <>
      {children}
      <CustomModal
        mode="popup"
        title=""
        onClose={() => setShowAspectRatioWarning(false)}
        openModal={showAspectRatioWarning}
        height="51.5rem"
        width="40rem"
        backdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(0.25rem)",
          },
        }}
      >
        <Box
          sx={{
            // background: Colors.WHITE,
            // borderRadius: "0.75rem",
            // padding: "5rem 3rem",
            // width: "38rem",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "3rem",
            alignItems: "center",
          }}
        >
          <ErrorIcon sx={{ fontSize: "12rem", color: Colors.SECONDARY }} />
          <Typography
            variant="h2"
            sx={{ fontWeight: 500, color: Colors.TEXT_GRAY_DARK }}
          >
            Warning
          </Typography>
          <Typography
            sx={{
              fontSize: "1.5rem",
              textAlign: "center",
              fontWeight: 500,
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            We recommend using 16:9 screen for better experience. in other
            aspect ratios, the UI might be not showing properly or will be
            cropped.
          </Typography>
          <CustomButton
            onClick={() => setShowAspectRatioWarning(false)}
            sx={{
              background: Colors.PRIMARY,
              mt: "2rem",
              fontSize: "2rem",
              padding: "0.75rem 3rem",
              borderRadius: "0.75rem",
              width: "11.3rem",
              maxWidth: "11.3rem",
              minWidth: "11.3rem",
            }}
          >
            Got it
          </CustomButton>
        </Box>
      </CustomModal>
      {/* <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          zIndex: 99999999,
          background: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(0.25rem)",
          display: showAspectRatioWarning ? "flex" : "none",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            background: Colors.WHITE,
            borderRadius: "0.75rem",
            padding: "5rem 3rem",
            width: "38rem",
            display: "flex",
            flexDirection: "column",
            gap: "3rem",
            alignItems: "center",
          }}
        >
          <ErrorIcon sx={{ fontSize: "12rem", color: Colors.SECONDARY }} />
          <Typography
            variant="h2"
            sx={{ fontWeight: 500, color: Colors.TEXT_GRAY_DARK }}
          >
            Warning
          </Typography>
          <Typography
            sx={{
              fontSize: "1.5rem",
              textAlign: "center",
              fontWeight: 500,
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            We recommend using 16:9 screen for better experience. in other
            aspect ratios, the UI might be not showing properly or will be
            cropped.
          </Typography>
          <CustomButton
            onClick={() => setShowAspectRatioWarning(false)}
            sx={{
              background: Colors.PRIMARY,
              mt: "2rem",
              fontSize: "2rem",
              padding: "0.75rem 3rem",
              borderRadius: "0.75rem",
            }}
          >
            Got it
          </CustomButton>
        </Box>
      </Box> */}

      {showFullScreenWarning && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[99999]" style={{ backdropFilter: "blur(4px)" }}>
          <div className="bg-white rounded-2xl shadow-xl p-10 w-[480px] relative flex flex-col items-center gap-6">
            <button
              onClick={() => setShowFullScreenWarning(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X size={22} />
            </button>

            <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
              <Maximize2 className="text-blue-500" size={36} />
            </div>

            <div className="flex flex-col items-center gap-3 text-center">
              <h2 className="text-2xl font-semibold text-gray-800">Full Screen Recommended</h2>
              <p className="text-lg text-gray-500 leading-relaxed">
                It's recommended to use full screen mode for a better experience.
              </p>
            </div>

            <button
              onClick={toggleFullScreen}
              className="w-full h-14 rounded-xl bg-brand hover:opacity-90 text-white font-semibold text-lg flex items-center justify-center gap-3 transition-opacity"
            >
              <Maximize2 size={22} />
              Open Full Screen
            </button>
          </div>
        </div>
      )}
      {/* <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          zIndex: 99999999,
          background: "rgba(0, 0, 0, 0.4)",
          backdropFilter: "blur(0.1rem)",
          display: showFullScreenWarning ? "flex" : "none",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            background: Colors.WHITE,
            borderRadius: "0.75rem",
            padding: "5rem 3rem",
            width: "38rem",
            display: "flex",
            flexDirection: "column",
            gap: "2rem",
            alignItems: "center",
          }}
        >
          {// <ErrorIcon sx={{ fontSize: "12rem", color: Colors.SECONDARY }} />
          }
          <IconButton
            sx={{
              color: Colors.WHITE,
              background: Colors.ACCENT_GRAY_DISABLED,
              "&:hover": {
                background: Colors.ACCENT_GRAY_DISABLED,
              },
              alignSelf: "flex-end",
              mt: "-3rem",
              mr: "-1rem",
              mb: "2rem",
              width: "3rem",
              height: "3rem",
            }}
            onClick={() => setShowFullScreenWarning(false)}
          >
            <CloseIcon
              sx={{
                fontSize: "2.2rem",
              }}
            />
          </IconButton>
          <Typography
            sx={{
              fontSize: "1.5rem",
              textAlign: "center",
              fontWeight: 500,
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            Its recommended to use full screen mode for better experience.
          </Typography>
          <CustomButton
            onClick={toggleFullScreen}
            sx={{
              background: Colors.PRIMARY,
              mt: "2rem",
              fontSize: "1.8rem",
              padding: "0.75rem 3rem",
              borderRadius: "0.75rem",
              display: "flex",
              alignItems: "center",
              gap: "1.5rem",
            }}
          >
            <OpenInFullIcon
              sx={{
                fontSize: "1.8rem",
                ml: "-0.9rem",
              }}
            />{" "}
            Open Full Screen
          </CustomButton>
        </Box>
      </Box> */}
    </>
  );
};

export default ResponsiveProvider;
